import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../../RH/Utils/Endpoints';
import { Models } from '../../../../RH/Utils/ModelsRelojChecador';
import { Models as ModelsGeneral } from '../../../../General/Utils/ModelsGeneral';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './TurnosModal';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';
import { getSessionEmpresaId } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';

const PAGE_SIZE = 10;
const nombreTabla = "Turnos";
const endpoint = ENDPOINTS.turnos;
const endpointById = ENDPOINTS.turnoById;
const modelo = Models.turno; // <- en tu ModelsGeneral ya está: turno: (t) => turnosNormalize(t)

const maps = {
  mapCreate: (f) => ({
    clave: f.clave,
    nombre: f.nombre,
    horaInicio: f.horaInicio,
    horaFin: f.horaFin,
    descripcion: f.descripcion,
    horaComidaInicio: f.horaComidaInicio,
    horaComidaFin: f.horaComidaFin,
    idEmpresa: getSessionEmpresaId() || null
  }),
  mapUpdate: (id, f) => ({
    idTurno: id,
    clave: f.clave,
    nombre: f.nombre,
    horaInicio: f.horaInicio,
    horaFin: f.horaFin,
    descripcion: f.descripcion,
    horaComidaInicio: f.horaComidaInicio,
    horaComidaFin: f.horaComidaFin,
    idEmpresa: getSessionEmpresaId() || null
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });
  const [empresas, setEmpresas] = useState([]);
  const [empresasMap, setEmpresasMap] = useState({});
  const [showHijas, setShowHijas] = useState(false);

  // ── Permisos granulares por recurso ──────────────────────────────────────
  const { puedeCrear, puedeEditar, puedeEliminar } = useResourcePermissions();

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(ENDPOINTS.turnosSecurity(showHijas ? 'hijas' : 'propias'));
      setRows((res.data || []).map((x) => modelo(x)));

      // Cargar empresas para el mapeo visual
      const resEmp = await axios.get(ENDPOINTS.empresas);
      const normalizedEmpresas = (resEmp.data || []).map(emp => ModelsGeneral.empresas(emp));
      setEmpresas(normalizedEmpresas);
      const mapE = {};
      normalizedEmpresas.forEach(emp => {
          mapE[emp.idEmpresa] = emp.nombre;
      });
      setEmpresasMap(mapE);
    } catch (e) {
      console.error(e);
      triggerAlert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [showHijas]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const clave = (r.clave || '').toLowerCase();
      const nombre = (r.nombre || '').toLowerCase();
      const descripcion = (r.descripcion || '').toLowerCase();
      const hi = (r.horaInicio || '').toLowerCase();
      const hf = (r.horaFin || '').toLowerCase();
      return (
        clave.includes(q) ||
        nombre.includes(q) ||
        descripcion.includes(q) ||
        hi.includes(q) ||
        hf.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idTurno === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idTurno ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      triggerAlert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por clave, nombre, horas o descripción…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                  className={`btn-filter ${showHijas ? 'active' : ''}`}
                  onClick={() => setShowHijas(!showHijas)}
                  title={showHijas ? "Ocultar turnos de empresas hijas" : "Ver turnos de empresas hijas"}
              >
                  {showHijas ? '👁️ Ver Propias' : '🌿 Ver Hijas'}
              </button>
              {puedeCrear && (
                <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
              )}
          </div>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Clave</th>
                  <th>Nombre</th>
                  <th>Hora inicio</th>
                  <th>Hora fin</th>
                   <th>Descripción</th>
                  <th>Empresa</th>
                  {puedeEditar && <th style={{ textAlign: 'center' }}>Editar</th>}
                  {puedeEliminar && <th style={{ textAlign: 'center' }}>Borrar</th>}
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idTurno}>
                    <td><strong>{normalizeValue(r.clave)}</strong></td>
                    <td>{normalizeValue(r.nombre)}</td>
                    <td>{normalizeValue(r.horaInicio)}</td>
                    <td>{normalizeValue(r.horaFin)}</td>
                    <td>{normalizeValue(r.descripcion)}</td>
                    <td>
                        <span style={{ fontSize: '0.85rem', color: r.idEmpresa ? '#334155' : '#94a3b8', fontWeight: r.idEmpresa ? 600 : 400 }}>
                            {r.idEmpresa ? (empresasMap[r.idEmpresa] || `ID: ${r.idEmpresa}`) : 'Global'}
                        </span>
                    </td>
                     {puedeEditar && (
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idTurno)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>
                    )}
                    {puedeEliminar && (
                      <td style={{ textAlign: 'center' }}>
                        {r.tieneEmpleados ? (
                          <span title="Bloqueado: Este turno tiene empleados asignados activos." style={{ cursor: 'not-allowed', opacity: 0.3, display: 'inline-block' }}>
                            <img src={sigmaDelete} alt="Bloqueado" className="rk-ico" style={{ filter: 'grayscale(100%)' }} />
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="rk-iconbtn rk-iconbtn--del"
                            onClick={() => {
                              setEditingRowId(null);
                              const name = r.nombre || 'Turno';
                              askDelete(r.idTurno, name);
                            }}
                            aria-label="Borrar"
                            title="Borrar"
                          >
                            <img src={sigmaDelete} alt="" className="rk-ico" />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                 {!loading && paged.length === 0 && (<tr><td colSpan={6 + (puedeEditar ? 1 : 0) + (puedeEliminar ? 1 : 0)}>Sin resultados.</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (<PageBtn key={n} label={n} onClick={() => setPage(n)} active={n === page} />))}
            <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}

